import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
} from "@mui/material";
import axios from "axios";
import { Country, State } from "country-state-city";
import React, { useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { getCookie } from "../../../../function";
import getFilterServices from "../../../../services/getFilterServices";
import getJournalService from "../../../../services/getJournalService";
import getPaymentModeService from "../../../../services/getPaymentModeService";

// Validation Schema
const validationSchema = Yup.object().shape({
  emailId: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  PublicationId: Yup.string().required("Publication is required"),
  articleid: Yup.string()
    .required("Article ID is required"),
    // .matches(
    //   /^[A-Za-z0-9_]+$/,
    //   "Article ID can only contain letters, numbers, and underscores (_). No spaces or special characters allowed."
    // ),
  journalName: Yup.string().required("Journal name is required"),
  state: Yup.string().required("State is required"),
  service: Yup.string().required("Service is required"),
  authorName: Yup.string().required("Author name is required"),
  authorGstNo: Yup.string()
    .nullable()
    .transform((value) => value || ""),
  authorUniversity: Yup.string().required("Author university is required"),
  remark: Yup.string()
    .nullable()
    .transform((value) => value || ""),
  dateofPayment: Yup.string().when("hasAddedFees", {
    is: true,
    then: (schema) => schema.required("Date of payment is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  baseFees: Yup.string().required("Base fees is required"),
  taxRate: Yup.string().required("Tax rate is required"),
  totalfees: Yup.string().required("Total fees is required"),
  feesRecived: Yup.string().when("hasAddedFees", {
    is: true,
    then: (schema) =>
      schema
        .required("Fees received is required")
        .matches(
          /^\d*\.?\d+$/,
          "Fees received must be a valid number."
        )
        .test(
          "not-zero",
          "Fees received cannot be 0",
          (value) => parseFloat(value) > 0
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  remaningFees: Yup.string().required("Remaining fees is required"),
  additionalAmountReceived: Yup.string(),
  feeType: Yup.string().required("Fee type is required"),
  publicationType: Yup.string().required("Publication type is required"),
  paymentStatus: Yup.string().required("Payment status is required"),
  country: Yup.string().required("Country is required"),
  currency: Yup.string().required("Currency is required"),
  payerName: Yup.string().required("Payer name is required"),
  invoiceType: Yup.string().required("Invoice type is required"),
  paymentMode: Yup.string().when("hasAddedFees", {
    is: true,
    then: (schema) => schema.required("Payment mode is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bankName: Yup.string().when("hasAddedFees", {
    is: true,
    then: (schema) => schema.required("Bank name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  taxAmount: Yup.string(),
  file: Yup.mixed().when("hasAddedFees", {
    is: true,
    then: (schema) => schema.required("Article file is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  paymentProof: Yup.array().when("hasAddedFees", {
    is: true,
    then: (schema) => schema.min(1, "At least one payment proof is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const Form = ({ getCategoryData, setLoadingCheck }) => {
  const [publicationList, setpublicationList] = useState([]);
  const [journalList, setJournalList] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [paymentModeList, setPaymentModeList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [selectedIsoCode, setSelectedIsoCode] = useState("");
  const [customJournalInput, setCustomJournalInput] = useState("");
  const [customServiceInput, setCustomServiceInput] = useState("");
  const [customPaymentModeInput, setCustomPaymentModeInput] = useState("");
  const [customBankInput, setCustomBankInput] = useState("");

  const navigate = useNavigate();
  const clientId = getCookie("id");

  const [showCustomService, setShowCustomService] = useState(false);
  const [showCustomJournal, setShowCustomJournal] = useState(false);
  const [showCustomPaymentMode, setShowCustomPaymentMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [journalAnchorEl, setJournalAnchorEl] = useState(null);
  const [paymentModeAnchorEl, setPaymentModeAnchorEl] = useState(null);
  const [bankAnchorEl, setBankAnchorEl] = useState(null);

  const serviceSelectRef = useRef(null);
  const journalSelectRef = useRef(null);
  const paymentModeSelectRef = useRef(null);
  const bankSelectRef = useRef(null);

  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [isJournalLoading, setIsJournalLoading] = useState(false);
  const [isPaymentModeLoading, setIsPaymentModeLoading] = useState(false);
  const [isBankLoading, setIsBankLoading] = useState(false);

  const [showFeesFields, setShowFeesFields] = useState(false);
  const [hasAddedFees, setHasAddedFees] = useState(false);

  // Form state for values, errors, and touched
  const initialValues = {
      emailId: "",
      PublicationId: "",
      articleid: "",
      journalName: "",
      journalId: "",
      state: "",
      service: "",
      serviceId: "",
      authorName: "",
      authorGstNo: "",
      authorUniversity: "",
      remark: "",
      dateofPayment: "",
      baseFees: "",
      taxRate: "0",
      totalfees: "0",
      feesRecived: "0",
      remaningFees: "0",
      additionalAmountReceived: "0",
      feeType: "",
      publicationType: "",
      paymentStatus: "",
      file: null,
      clientId: clientId || "",
      paymentProof: [],
      country: "",
      currency: "INR",
      payerName: "",
      sameAsAuthor: false,
      invoiceType: "",
      taxAmount: "0.00",
      paymentMode: "",
      paymentModeId: "",
      bank: "",
      bankId: "",
  };
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper to validate a single field
  const validateField = async (field, value) => {
    try {
      await validationSchema.validateAt(field, { ...values, [field]: value });
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [field]: err.message }));
    }
  };

  // Helper to validate all fields
  const validateAll = async () => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err.inner) {
        const newErrors = {};
        err.inner.forEach((e) => {
          if (!newErrors[e.path]) newErrors[e.path] = e.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  const fetchData = async () => {
    try {
      console.log("calling.....");
      // Fetch publication list
      const list = await getJournalService.GetAllJournal();
      setpublicationList(list);

      // Fetch service list
      const serviceResponse = await getFilterServices.GetAllService();
      const journalResponse = await getFilterServices.GetAllManualJournal();
      const paymentModeResponse =
        await getPaymentModeService.GetAllPaymentModes();
      const bankResponse = await getPaymentModeService.GetAllBanks();

      if (serviceResponse && serviceResponse.length > 0) {
        setServiceList(serviceResponse);
      }
      if (journalResponse && journalResponse.length > 0) {
        setJournalList(journalResponse);
      }
      if (paymentModeResponse && paymentModeResponse.length > 0) {
        setPaymentModeList(paymentModeResponse);
      }
      if (bankResponse && bankResponse.length > 0) {
        setBankList(bankResponse);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Keep existing lists if fetch fails
      if (serviceList.length === 0) {
        setServiceList([]);
      }
      if (journalList.length === 0) {
        setJournalList([]);
      }
      if (paymentModeList.length === 0) {
        setPaymentModeList([]);
      }
      if (bankList.length === 0) {
        setBankList([]);
      }
    }
  };

  // Load states when country changes
  useEffect(() => {
    if (selectedIsoCode) {
      const countryStates = State.getStatesOfCountry(selectedIsoCode);
      setStates(countryStates);
    } else {
      setStates([]);
    }
  }, [selectedIsoCode]);

  // Add calculation functions
  const calculateTotalFees = (base, taxRate) => {
    const baseNum = parseFloat(base) || 0;
    const taxRateNum = parseFloat(taxRate) || 0;
    const tax = (baseNum * taxRateNum) / 100;
    setValues((prev) => ({ ...prev, taxAmount: tax.toFixed(2) }));
    return baseNum + tax;
  };

  const calculateRemaningFees = (total, paid) => {
    const totalNum = parseFloat(total) || 0;
    const paidNum = parseFloat(paid) || 0;
    // return totalNum - paidNum;
    const remaining = totalNum - paidNum;

  // Treat tiny differences like -0.0001 or 0.3 as 0
  return Math.abs(remaining) < 0.5 ? 0 : remaining; 



  };

  // Modify handleChange to handle country selection
  const handleChange = async (e) => {
    const { name, value } = e.target;
    // Special handling for country selection
    if (name === "country") {
      const selectedCountry = countries.find((c) => c.isoCode === value);
      setSelectedIsoCode(value);
      setSelectedCountryName(selectedCountry ? selectedCountry.name : "");
      setValues((prev) => ({ ...prev, country: selectedCountry ? selectedCountry.name : "" }));
      setValues((prev) => ({ ...prev, state: "" }));
      await validateField("country", selectedCountry ? selectedCountry.name : "");
      setTouched((prev) => ({ ...prev, country: true }));
    } else {
      setValues((prev) => ({ ...prev, [name]: value }));
      await validateField(name, value);
      setTouched((prev) => ({ ...prev, [name]: true }));
    }

    // If sameAsAuthor is checked and author name changes, update payer name
    if (name === "authorName" && values.sameAsAuthor) {
      setValues((prev) => ({ ...prev, payerName: value }));
      await validateField("payerName", value);
      setTouched((prev) => ({ ...prev, payerName: true }));
    }

    // Handle calculations based on field changes
    if (name === "baseFees") {
      const total = calculateTotalFees(value, values.taxRate);
      setValues((prev) => ({ ...prev, totalfees: total.toFixed(2) }));
      const remaining = calculateRemaningFees(total, values.feesRecived);
      setValues((prev) => ({ ...prev, remaningFees: remaining.toFixed(2) }));
      if (remaining === 0) {
        setValues((prev) => ({ ...prev, paymentStatus: "true" }));
      } else {
        setValues((prev) => ({ ...prev, paymentStatus: "false" }));
      }
    } else if (name === "taxRate") {
      const total = calculateTotalFees(values.baseFees, value);
      setValues((prev) => ({ ...prev, totalfees: total.toFixed(2) }));
      const remaining = calculateRemaningFees(total, values.feesRecived);
      setValues((prev) => ({ ...prev, remaningFees: remaining.toFixed(2) }));
      if (remaining === 0) {
        setValues((prev) => ({ ...prev, paymentStatus: "true" }));
      } else {
        setValues((prev) => ({ ...prev, paymentStatus: "false" }));
      }
    } else if (name === "feesRecived") {
      const totalFees = parseFloat(values.totalfees) || 0;
      const feesReceived = parseFloat(value) || 0;
      const remaining = totalFees - feesReceived;
      if (remaining < 0) {
        setValues((prev) => ({ ...prev, remaningFees: "0" }));
        setValues((prev) => ({ ...prev, additionalAmountReceived: Math.abs(remaining).toFixed(2) }));
        setValues((prev) => ({ ...prev, paymentStatus: "true" }));
      } else {
        setValues((prev) => ({ ...prev, remaningFees: remaining.toFixed(2) }));
        setValues((prev) => ({ ...prev, additionalAmountReceived: "0" }));
        setValues((prev) => ({ ...prev, paymentStatus: remaining === 0 ? "true" : "false" }));
      }
      if (feesReceived === 0) {
        setValues((prev) => ({ ...prev, dateofPayment: "" }));
        setValues((prev) => ({ ...prev, paymentMode: "" }));
        setValues((prev) => ({ ...prev, bankName: "" }));
        setValues((prev) => ({ ...prev, file: null }));
        setValues((prev) => ({ ...prev, paymentProof: [] }));
      }
    }
  };

  const handleJournalChange = (e) => {
    const value = e.target.value;
    if (value === "Others") {
      setJournalAnchorEl(journalSelectRef.current);
    } else {
      setJournalAnchorEl(null);
      const selectedJournal = journalList.find(
        (journal) => journal.journalid === value
      );
      setValues((prev) => ({ ...prev, journalName: selectedJournal?.journalName || "" }));
      setValues((prev) => ({ ...prev, journalId: value }));
    }
  };

  const handleCustomJournalInput = (e) => {
    const value = e.target.value;
    setCustomJournalInput(value);

    // Check if input matches existing journal
    const journalExists = journalList.some(
      (journal) => journal.journalName.toLowerCase() === value.toLowerCase()
    );

    if (journalExists) {
      setErrors((prev) => ({ ...prev, journalName: "This journal already exists" }));
    } else {
      setErrors((prev) => ({ ...prev, journalName: "" }));
    }
  };

  const handleAddCustomJournal = async () => {
    if (!customJournalInput.trim()) return;

    // Check if journal already exists
    const journalExists = journalList.some(
      (journal) =>
        journal.journalName.toLowerCase() === customJournalInput.toLowerCase()
    );

    if (journalExists) {
      NotificationManager.error("This journal already exists in the list");
      return;
    }

    try {
      setIsJournalLoading(true);
      // Add to API
      const result = await getFilterServices.AddManualJournal({
        journalName: customJournalInput,
      });

      console.log("result", result);

      if (result && result.additionalData) {
        const newJournal = {
          journalid: result.additionalData.journalid,
          journalName: customJournalInput,
        };

        // Add to journal list
        setJournalList([...journalList, newJournal]);

        // Set both journal name and ID in formik
        setValues((prev) => ({ ...prev, journalName: newJournal.journalName, journalId: newJournal.journalid }));

        // Reset custom input and close popover
        setCustomJournalInput("");
        setJournalAnchorEl(null);
      }
    } catch (error) {
      console.error("Error adding custom journal:", error);
      NotificationManager.error("Failed to add journal");
    } finally {
      setIsJournalLoading(false);
    }
  };

  const handleCloseJournalPopover = () => {
    setJournalAnchorEl(null);
    setCustomJournalInput("");
    setErrors((prev) => ({ ...prev, journalName: "" }));
  };

  const handleServiceChange = (e) => {
    const value = e.target.value;
    if (value === "Others") {
      setAnchorEl(serviceSelectRef.current);
    } else {
      setAnchorEl(null);
      const selectedService = serviceList.find(
        (service) => service.serviceId === value
      );
      setValues((prev) => ({ ...prev, service: selectedService?.serviceName || "" }));
      setValues((prev) => ({ ...prev, serviceId: value }));
    }
  };

  const handleCustomServiceInput = (e) => {
    const value = e.target.value;
    setCustomServiceInput(value);

    // Check if input matches existing service
    const serviceExists = serviceList.some(
      (service) => service.serviceName.toLowerCase() === value.toLowerCase()
    );

    if (serviceExists) {
      setErrors((prev) => ({ ...prev, service: "This service already exists" }));
    } else {
      setErrors((prev) => ({ ...prev, service: "" }));
    }
  };

  const handleAddCustomService = async () => {
    if (!customServiceInput.trim()) return;

    // Check if service already exists
    const serviceExists = serviceList.some(
      (service) =>
        service.serviceName.toLowerCase() === customServiceInput.toLowerCase()
    );

    if (serviceExists) {
      NotificationManager.error("This service already exists in the list");
      return;
    }

    try {
      setIsServiceLoading(true);
      // Add to API
      const result = await getFilterServices.AddService({
        serviceName: customServiceInput,
      });

      // let parsedData = JSON.parse(result);
      // let response = JSON.parse(parsedData.body);

      if (result && result.additionalData) {
        const newService = {
          serviceId: result.additionalData.serviceId,
          serviceName: customServiceInput,
        };

        setServiceList([...serviceList, newService]);

        // Set both service name and ID
        setValues((prev) => ({ ...prev, service: newService.serviceName, serviceId: newService.serviceId }));

        // Reset custom input and close popover
        setCustomServiceInput("");
        setAnchorEl(null);

        // NotificationManager.success("Service added successfully");
      }
    } catch (error) {
      console.error("Error adding custom service:", error);
      NotificationManager.error("Failed to add service");
    } finally {
      setIsServiceLoading(false);
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setCustomServiceInput("");
    setErrors((prev) => ({ ...prev, service: "" }));
  };

  const handlePaymentModeChange = (e) => {
    const value = e.target.value;
    if (value === "Others") {
      setPaymentModeAnchorEl(paymentModeSelectRef.current);
    } else {
      setPaymentModeAnchorEl(null);
      const selectedPaymentMode = paymentModeList.find(
        (mode) => mode.paymentModeId === value
      );
      setValues((prev) => ({ ...prev, paymentMode: selectedPaymentMode?.paymentModeName || "" }));
      setValues((prev) => ({ ...prev, paymentModeId: value }));
    }
  };

  const handleCustomPaymentModeInput = (e) => {
    const value = e.target.value;
    setCustomPaymentModeInput(value);

    // Check if input matches existing payment mode
    const paymentModeExists = paymentModeList.some(
      (mode) => mode.paymentModeName.toLowerCase() === value.toLowerCase()
    );

    if (paymentModeExists) {
      setErrors((prev) => ({ ...prev, paymentMode: "This payment mode already exists" }));
    } else {
      setErrors((prev) => ({ ...prev, paymentMode: "" }));
    }
  };

  const handleAddCustomPaymentMode = async () => {
    if (!customPaymentModeInput.trim()) return;

    // Check if payment mode already exists
    const paymentModeExists = paymentModeList.some(
      (mode) =>
        mode.paymentModeName.toLowerCase() ===
        customPaymentModeInput.toLowerCase()
    );

    if (paymentModeExists) {
      NotificationManager.error("This payment mode already exists in the list");
      return;
    }

    try {
      setIsPaymentModeLoading(true);
      // Add to API
      const result = await getPaymentModeService.AddPaymentMode({
        paymentModeName: customPaymentModeInput,
      });

      // let parsedData = JSON.parse(result);
      // let response = JSON.parse(parsedData.body);

      console.log("Resultpayment", result);

      if (result && result.additionalData) {
        const newPaymentMode = {
          paymentModeId: result.additionalData.paymentModeId,
          paymentModeName: customPaymentModeInput,
        };
        setPaymentModeList([...paymentModeList, newPaymentMode]);

        // Set both payment mode name and ID
        setValues((prev) => ({ ...prev, paymentMode: newPaymentMode.paymentModeName, paymentModeId: newPaymentMode.paymentModeId }));

        // Reset custom input and close popover
        setCustomPaymentModeInput("");
        setPaymentModeAnchorEl(null);

        // NotificationManager.success("Payment mode added successfully");
      }
    } catch (error) {
      console.error("Error adding custom payment mode:", error);
      NotificationManager.error("Failed to add payment mode");
    } finally {
      setIsPaymentModeLoading(false);
    }
  };

  const handleClosePaymentModePopover = () => {
    setPaymentModeAnchorEl(null);
    setCustomPaymentModeInput("");
    setErrors((prev) => ({ ...prev, paymentMode: "" }));
  };

  const handleBankChange = (e) => {
    const value = e.target.value;
    if (value === "Others") {
      setBankAnchorEl(bankSelectRef.current);
    } else {
      setBankAnchorEl(null);
      const selectedBank = bankList.find((bank) => bank.bankId === value);
      setValues((prev) => ({ ...prev, bankName: selectedBank?.bankName || "" }));
      setValues((prev) => ({ ...prev, bankId: value }));
    }
  };

  const handleCustomBankInput = (e) => {
    const value = e.target.value;
    setCustomBankInput(value);

    // Check if input matches existing bank
    const bankExists = bankList.some(
      (bank) => bank.bankName.toLowerCase() === value.toLowerCase()
    );

    if (bankExists) {
      setErrors((prev) => ({ ...prev, bankName: "This bank already exists" }));
    } else {
      setErrors((prev) => ({ ...prev, bankName: "" }));
    }
  };

  const handleAddCustomBank = async () => {
    if (!customBankInput.trim()) return;

    // Check if bank already exists
    const bankExists = bankList.some(
      (bank) => bank.bankName.toLowerCase() === customBankInput.toLowerCase()
    );

    if (bankExists) {
      NotificationManager.error("This bank already exists in the list");
      return;
    }

    try {
      setIsBankLoading(true);
      // Add to API
      const result = await getPaymentModeService.AddBank({
        bankName: customBankInput,
      });

      if (result && result.additionalData) {
        const newBank = {
          bankId: result.additionalData.bankId,
          bankName: customBankInput,
        };
        setBankList([...bankList, newBank]);

        // Set both bank name and ID
        setValues((prev) => ({ ...prev, bankName: newBank.bankName, bankId: newBank.bankId }));

        // Reset custom input and close popover
        setCustomBankInput("");
        setBankAnchorEl(null);

        // NotificationManager.success("Bank added successfully");
      }
    } catch (error) {
      console.error("Error adding custom bank:", error);
      NotificationManager.error("Failed to add bank");
    } finally {
      setIsBankLoading(false);
    }
  };

  const handleCloseBankPopover = () => {
    setBankAnchorEl(null);
    setCustomBankInput("");
    setErrors((prev) => ({ ...prev, bankName: "" }));
  };

  // Add useEffect to maintain lists
  useEffect(() => {
    if (
      serviceList.length === 0 ||
      journalList.length === 0 ||
      paymentModeList.length === 0 ||
      bankList.length === 0
    ) {
      fetchData();
    }
  }, [
    serviceList.length,
    journalList.length,
    paymentModeList.length,
    bankList.length,
  ]);

  // Add handler for checkbox
  const handleSameAsAuthorChange = (e) => {
    const checked = e.target.checked;
    setValues((prev) => ({ ...prev, sameAsAuthor: checked }));
    if (checked) {
      setValues((prev) => ({ ...prev, payerName: values.authorName }));
      setErrors((prev) => ({ ...prev, payerName: undefined }));
      setTouched((prev) => ({ ...prev, payerName: false }));
    }
  };

  const handleFeesButtonClick = () => {
    if (!showFeesFields) {
      setShowFeesFields(true);
      setHasAddedFees(true);
    } else {
      setShowFeesFields(false);
      setHasAddedFees(false);
      // Reset fees related fields
      setValues((prev) => ({ ...prev, feesRecived: "0", remaningFees: prev.totalfees, paymentStatus: "false", paymentMode: "", bankName: "", dateofPayment: "", additionalAmountReceived: "0" }));
    }
  };

  // Handle blur for fields
  const handleBlur = async (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    await validateField(name, values[name]);
  };

  // Helper: get required error message for a field
  const getRequiredError = (field) => {
    if (touched[field] && (!values[field] || values[field] === "")) {
      return "This field is required";
    }
    return errors[field] || "";
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTouched(Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    const isValid = await validateAll();
    if (!isValid) {
      setIsSubmitting(false);
      NotificationManager.error("Please fill all the details.");
      return;
    }

    if (!clientId) {
      NotificationManager.error("Client ID is not defined. Please log in.");
      setIsSubmitting(false);
      return;
    }

    try {
      setLoadingCheck(true);
      // Validate required fields based on fees received
      const baseRequiredFields = [
        "emailId",
        "authorUniversity",
        "PublicationId",
        "articleid",
        "journalName",
        "service",
        "totalfees",
        "feesRecived",
        "remaningFees",
        "feeType",
        "country",
        "publicationType",
        "paymentStatus",
        "payerName",
        "invoiceType",
      ];
      const paymentRequiredFields =
        parseFloat(values.feesRecived) > 0
          ? [
              "dateofPayment",
              "paymentMode",
              "bankName",
              "file",
              "paymentProof",
            ]
          : [];
      const requiredFields = [
        ...baseRequiredFields,
        ...paymentRequiredFields,
      ];
      const missingFields = requiredFields.filter((field) => !values[field]);
      if (missingFields.length > 0) {
        // Mark all missing fields as touched to show errors
        const touchedUpdate = {};
        missingFields.forEach((field) => {
          touchedUpdate[field] = true;
        });
        setTouched((prev) => ({ ...prev, ...touchedUpdate }));
        NotificationManager.error("Please fill all the details.");
        setLoadingCheck(false);
        setIsSubmitting(false);
        return;
      }

      // Rest of your submission code...
      const articleFile = values.file;
      const proofFiles = values.paymentProof;

      // Validate article file name if provided
      if (articleFile) {
        const isValidName =
          !(
            articleFile.name.includes("$") ||
            articleFile.name.includes("#") ||
            articleFile.name.includes("&") ||
            articleFile.name.includes("@")
          ) && articleFile.name.length >= 4;

        if (!isValidName) {
          alert(
            "Filename should not contain special characters like $, #, &, @"
          );
          setIsSubmitting(false);
          return;
        }
      }

      // 1) UPLOAD ARTICLE FILE TO S3 VIA PRESIGNED URL
      let articleFileUrl = "";
      if (articleFile) {
        const presignResponse = await axios.post(
          "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles/presignedurl",
          {
            fileName: articleFile.name,
            fileType: articleFile.type,
            folderName: "articles",
          },
          {
            headers: {
              Authorization: getCookie("token"),
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            },
          }
        );
        const presignData =
          typeof presignResponse.data === "string"
            ? JSON.parse(presignResponse.data)
            : presignResponse.data;
        const { presignedUrl, fileKey } = presignData;
        if (!presignedUrl) {
          throw new Error("Presigned URL for article file is undefined");
        }
        await axios.put(presignedUrl, articleFile, {
          headers: {
            "Content-Type": articleFile.type,
          },
        });
        articleFileUrl = fileKey;
      }

      // 2) UPLOAD PAYMENT PROOFS (IF ANY) USING PRESIGNED URL
      let paymentProofUrls = [];
      if (proofFiles && proofFiles.length > 0) {
        for (const proofFile of proofFiles) {
          const presignResponse = await axios.post(
            "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles/presignedurl",
            {
              fileName: proofFile.name,
              fileType: proofFile.type,
              folderName: "paymentProofs",
            },
            {
              headers: {
                Authorization: getCookie("token"),
                "Content-Type": "application/json",
                "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
              },
            }
          );
          const presignData =
            typeof presignResponse.data === "string"
              ? JSON.parse(presignResponse.data)
              : presignResponse.data;
          const { presignedUrl, fileKey } = presignData;
          if (!presignedUrl) {
            throw new Error(
              "Presigned URL for payment proof file is undefined"
            );
          }
          await axios.put(presignedUrl, proofFile, {
            headers: { "Content-Type": proofFile.type },
          });
          paymentProofUrls.push(fileKey);
        }
      }

      // 3) NOW SUBMIT THE ARTICLE METADATA (INCLUDING S3 KEYS)
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key !== "file") {
          const value =
            values[key] === null || values[key] === undefined
              ? ""
              : values[key];
          formData.append(key, value);
        }
      });
      formData.append("articleFileUrl", articleFileUrl);
      formData.append("paymentProof", JSON.stringify(paymentProofUrls));
      let response, result, parsedData;
      try {
        response = await axios.post(
          "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: getCookie("token"),
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            },
          }
        );
        result = JSON.parse(response.data);
        parsedData = JSON.parse(result.body);
      } catch (err) {
        // If backend returns error for existing article ID, show notification but do not reset the form
        if (err.response && err.response.data) {
          try {
            const backendResult = JSON.parse(err.response.data);
            const backendParsed = JSON.parse(backendResult.body);
            if (backendParsed && backendParsed.message) {
              NotificationManager.error(backendParsed.message);
              setLoadingCheck(false);
              setIsSubmitting(false);
              return;
            }
          } catch (parseErr) {}
        }
        NotificationManager.error("An error occurred while submitting the form");
        setLoadingCheck(false);
        setIsSubmitting(false);
        return;
      }
      if (parsedData?.success) {
        NotificationManager.success(parsedData.message, "Successful");
        setTimeout(() => {
          navigate("/client/Articles");
        }, 1000);
      } else {
        NotificationManager.info(parsedData.message, "Failed");
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      NotificationManager.error(
        "An error occurred while submitting the form"
      );
    } finally {
      setLoadingCheck(false);
      setIsSubmitting(false);
    }
  };

  // Map field keys to user-friendly labels (move this above handleSubmit for scope)
  const fieldLabels = {
    emailId: "Author Email",
    PublicationId: "Publication",
    articleid: "Article ID",
    journalName: "Journal Name",
    service: "Service",
    totalfees: "Total Fees",
    feesRecived: "Fees Received",
    remaningFees: "Remaining Fees",
    feeType: "Fee Type",
    country: "Country",
    publicationType: "Publication Type",
    paymentStatus: "Payment Status",
    payerName: "Payer Name",
    invoiceType: "Invoice Type",
    dateofPayment: "Date of Payment",
    paymentMode: "Payment Mode",
    bankName: "Bank Name",
    file: "Invoice File",
    paymentProof: "Payment Proof",
    authorName: "Author Name",
    authorGstNo: "Author GST Number",
    authorUniversity: "Author University",
    remark: "Remark",
    state: "State",
    currency: "Currency",
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3">
      {/* Basic Information Section */}
      <div className="form-section mb-4">
        <h4 className="section-title mb-3">Basic Information</h4>
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <TextField
              size="small"
              fullWidth
              id="emailId"
              name="emailId"
              label="Author Email"
              value={values.emailId}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.emailId && Boolean(errors.emailId)}
              helperText={touched.emailId && errors.emailId}
            />
          </div>
          <div className="w-50 ms-5">
            <TextField
              size="small"
              fullWidth
              id="articleId"
              name="articleid"
              label="Article Id"
              value={values.articleid}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.articleid && Boolean(errors.articleid)
              }
              helperText={touched.articleid && errors.articleid}
            />
          </div>
        </div>

        {/* Author Information */}
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <TextField
              size="small"
              fullWidth
              id="authorName"
              name="authorName"
              label="Author Name"
              value={values.authorName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.authorName && Boolean(errors.authorName)
              }
              helperText={touched.authorName && errors.authorName}
            />
          </div>
          <div className="w-50 ms-5">
            <TextField
              size="small"
              fullWidth
              id="authorGstNo"
              name="authorGstNo"
              label="Author GST Number"
              value={values.authorGstNo}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.authorGstNo && Boolean(errors.authorGstNo)
              }
              helperText={
                touched.authorGstNo && errors.authorGstNo
              }
            />
          </div>
        </div>

        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <TextField
              size="small"
              fullWidth
              id="authorUniversity"
              name="authorUniversity"
              label="Author University"
              value={values.authorUniversity}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.authorUniversity &&
                Boolean(errors.authorUniversity)
              }
              helperText={
                touched.authorUniversity &&
                errors.authorUniversity
              }
            />
          </div>
          <div className="w-50 ms-5">
            <FormControl fullWidth size="small">
              <InputLabel id="service-select-label">Select Service</InputLabel>
              <Select
                ref={serviceSelectRef}
                labelId="service-select-label"
                id="service-select"
                value={values.serviceId}
                onChange={handleServiceChange}
                label="Select Service"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {serviceList.map((service) => (
                  <MenuItem key={service.serviceId} value={service.serviceId}>
                    {service.serviceName}
                  </MenuItem>
                ))}
                <MenuItem value="Others">Others</MenuItem>
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.service && errors.service}
                </p>
              </FormHelperText>
            </FormControl>

            {/* Service Popover */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <div style={{ padding: "16px", minWidth: "300px" }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Enter New Service"
                  value={customServiceInput}
                  onChange={handleCustomServiceInput}
                  style={{ marginBottom: "16px" }}
                  disabled={isServiceLoading}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleClosePopover}
                    disabled={isServiceLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddCustomService}
                    disabled={!customServiceInput.trim() || isServiceLoading}
                  >
                    {isServiceLoading ? "Adding..." : "Add Service"}
                  </Button>
                </div>
              </div>
            </Popover>
          </div>
        </div>

        {/* Publication and Journal Selection */}
        <div className="form-group mt-4 d-flex">
          <div className="form-group w-50">
            <Autocomplete
              id="publication-select"
              className="select-publication"
              value={
                publicationList.find(
                  (journal) => journal.id === values.PublicationId
                ) || null
              }
              onChange={(event, newValue) => {
                setValues((prev) => ({ ...prev, PublicationId: newValue?.id || "" }));
              }}
              options={publicationList}
              getOptionLabel={(option) => option?.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Publication"
                  size="small"
                  fullWidth
                  variant="outlined"
                  className="select-journal"
                  placeholder="Search and Select Publication"
                  error={
                    touched.PublicationId &&
                    Boolean(errors.PublicationId)
                  }
                  helperText={
                    touched.PublicationId && errors.PublicationId
                  }
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableClearable
            />
          </div>

          <div className="w-50 ms-5">
            <FormControl fullWidth size="small" error={touched.journalId && Boolean(getRequiredError("journalId"))}>
              <InputLabel id="journal-select-label">Select Journal</InputLabel>
              <Select
                ref={journalSelectRef}
                labelId="journal-select-label"
                id="journal-select"
                value={values.journalId}
                onChange={handleJournalChange}
                label="Select Journal"
                onBlur={handleBlur}
                name="journalId"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {journalList.map((journal) => (
                  <MenuItem key={journal.journalid} value={journal.journalid}>
                    {journal.journalName}
                  </MenuItem>
                ))}
                <MenuItem value="Others">Others</MenuItem>
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {getRequiredError("journalId") === "The schema does not contain the path: journalId. (failed at: undefined which is a type: \"object\")" ? "" : getRequiredError("journalId")}
                </p>
              </FormHelperText>
            </FormControl>

            {/* Journal Popover */}
            <Popover
              open={Boolean(journalAnchorEl)}
              anchorEl={journalAnchorEl}
              onClose={handleCloseJournalPopover}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <div style={{ padding: "16px", minWidth: "300px" }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Enter New Journal"
                  value={customJournalInput}
                  onChange={handleCustomJournalInput}
                  style={{ marginBottom: "16px" }}
                  disabled={isJournalLoading}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleCloseJournalPopover}
                    disabled={isJournalLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddCustomJournal}
                    disabled={!customJournalInput.trim() || isJournalLoading}
                  >
                    {isJournalLoading ? "Adding..." : "Add Journal"}
                  </Button>
                </div>
              </div>
            </Popover>
          </div>
        </div>

        {/* Publication Type */}
        <div className="form-group mt-4 w-50 ">
          <FormControl fullWidth size="small">
            <InputLabel id="publication-type-label">
              Publication Type
            </InputLabel>
            <Select
              labelId="publication-type-label"
              id="publicationType"
              name="publicationType"
              value={values.publicationType}
              onChange={handleChange}
              label="Publication Type"
              onBlur={handleBlur}
              error={
                touched.publicationType &&
                Boolean(errors.publicationType)
              }
            >
              <MenuItem value="Special">Special</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
            </Select>
            <FormHelperText className="danger">
              <p style={{ color: "#d32f2f" }}>
                {touched.publicationType &&
                  errors.publicationType}
              </p>
            </FormHelperText>
          </FormControl>
        </div>
      </div>

      {/* Location Details Section */}
      <div className="form-section mb-4">
        <h4 className="section-title mb-3">Location Details</h4>
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <FormControl fullWidth size="small">
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                name="country"
                value={selectedIsoCode}
                onChange={handleChange}
                label="Country"
                onBlur={handleBlur}
                error={touched.country && Boolean(errors.country)}
              >
                <MenuItem value="">
                  <em>Select Country</em>
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.country && errors.country}
                </p>
              </FormHelperText>
            </FormControl>
          </div>

          <div className="w-50 ms-5">
            <FormControl fullWidth size="small">
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                name="state"
                value={values.state}
                onChange={handleChange}
                label="State"
                onBlur={handleBlur}
                disabled={!selectedIsoCode}
                error={touched.state && Boolean(errors.state)}
              >
                <MenuItem value="">
                  <em>Select State</em>
                </MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.isoCode} value={state.name}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.state && errors.state}
                </p>
              </FormHelperText>
            </FormControl>
          </div>
        </div>
      </div>

      {/* Payment Details Section */}
      <div className="form-section mb-4">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4 className="section-title mb-3">Payment Details</h4>

          {/* Add Fees Button */}
          <div className="form-group mt-4" style={{ marginBottom: "20px" }}>
            <Button
              variant="contained"
              color={showFeesFields ? "error" : "success"}
              onClick={handleFeesButtonClick}
            >
              {showFeesFields ? "Remove Fees" : "Add Fees"}
            </Button>
          </div>
        </div>

        {/* Base Fees and Tax Rate */}
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <TextField
              size="small"
              fullWidth
              id="baseFees"
              name="baseFees"
              label="Basic Fees"
              type="number"
              value={values.baseFees}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.baseFees && Boolean(errors.baseFees)}
              helperText={touched.baseFees && errors.baseFees}
              inputProps={{ min: "0.01", step: "0.01" }}
            />
          </div>
          <div className="w-50 ms-5">
            <FormControl fullWidth size="small">
              <InputLabel id="tax-rate-label">Tax Rate</InputLabel>
              <Select
                labelId="tax-rate-label"
                id="taxRate"
                name="taxRate"
                value={values.taxRate}
                onChange={handleChange}
                label="Tax Rate"
                onBlur={handleBlur}
                error={touched.taxRate && Boolean(errors.taxRate)}
              >
                <MenuItem value="0">0%</MenuItem>
                <MenuItem value="5">5%</MenuItem>
                <MenuItem value="18">18%</MenuItem>
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.taxRate && errors.taxRate}
                </p>
              </FormHelperText>
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "0.875rem",
                  color: "#666",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Tax Amount:</span>
                <span style={{ fontWeight: 500 }}>
                  {values.taxAmount || "0.00"} {values.currency}
                </span>
              </div>
            </FormControl>
          </div>
        </div>

        {/* Total Fees and Currency */}
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <TextField
              size="small"
              fullWidth
              id="totalfees"
              name="totalfees"
              label="Total Fees (Including Tax)"
              value={values.totalfees}
              InputProps={{
                readOnly: true,
              }}
              disabled={true}
              error={
                touched.totalfees && Boolean(errors.totalfees)
              }
              helperText={touched.totalfees && errors.totalfees}
            />
          </div>
          <div className="w-50 ms-5">
            <FormControl fullWidth size="small">
              <InputLabel id="currency-label">Currency</InputLabel>
              <Select
                labelId="currency-label"
                id="currency"
                name="currency"
                value={values.currency}
                onChange={handleChange}
                label="Currency"
                onBlur={handleBlur}
                error={
                  touched.currency && Boolean(errors.currency)
                }
              >
                <MenuItem value="INR">INR (₹)</MenuItem>
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="AED">AED (د.إ)</MenuItem>
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.currency && errors.currency}
                </p>
              </FormHelperText>
            </FormControl>
          </div>
        </div>

        {/* Fee Type */}
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <FormControl fullWidth size="small">
              <InputLabel id="fee-type-label">Fee Type</InputLabel>
              <Select
                labelId="fee-type-label"
                id="feeType"
                name="feeType"
                value={values.feeType}
                label="Fee Type"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.feeType && Boolean(errors.feeType)}
              >
                <MenuItem value="Author">Author</MenuItem>
                <MenuItem value="Agent">Agent</MenuItem>
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.feeType && errors.feeType}
                </p>
              </FormHelperText>
            </FormControl>
          </div>
          <div className="w-50 ms-5">
            <FormControl fullWidth size="small">
              <InputLabel id="invoice-type-label">Invoice Type</InputLabel>
              <Select
                labelId="invoice-type-label"
                id="invoiceType"
                name="invoiceType"
                value={values.invoiceType}
                onChange={handleChange}
                label="Invoice Type"
                onBlur={handleBlur}
                error={
                  touched.invoiceType &&
                  Boolean(errors.invoiceType)
                }
              >
                <MenuItem value="CRE Inhouse">CRE Inhouse</MenuItem>
                <MenuItem value="CRE Indian">CRE Indian</MenuItem>
                <MenuItem value="CRE Foreign">CRE Foreign</MenuItem>
              </Select>
              <FormHelperText className="danger">
                <p style={{ color: "#d32f2f" }}>
                  {touched.invoiceType && errors.invoiceType}
                </p>
              </FormHelperText>
            </FormControl>
          </div>
        </div>

        {/* Payer Name  */}
        <div className="form-group mt-4 d-flex">
          <div className="w-50">
            <TextField
              size="small"
              fullWidth
              id="payerName"
              name="payerName"
              label="Payer Name"
              value={values.payerName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.payerName && Boolean(getRequiredError("payerName"))}
              helperText={getRequiredError("payerName")}
              disabled={values.sameAsAuthor}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.sameAsAuthor}
                  onChange={handleSameAsAuthorChange}
                  name="sameAsAuthor"
                />
              }
              label="Same as Author Name"
            />
          </div>
          <div className="w-50 ms-5"></div>
        </div>
      </div>

      {/* Fees Details Section - Only shown when Add Fees is clicked */}
      {showFeesFields && (
        <div className="form-section mb-4">
          <h4 className="section-title mb-3">Fees Details</h4>

          {/* Fees Received and Remaining Fees */}
          <div className="form-group mt-4 d-flex">
            <div className="w-50">
              <TextField
                size="small"
                fullWidth
                id="feesRecived"
                name="feesRecived"
                label="Fees Received"
                value={values.feesRecived}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.feesRecived &&
                  Boolean(errors.feesRecived)
                }
                helperText={
                  touched.feesRecived && errors.feesRecived
                }
                type="number"
                inputProps={{
                  min: "0.01",
                  step: "0.01",
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                }}
              />
            </div>
            <div className="w-50 ms-5">
              <TextField
                size="small"
                fullWidth
                id="remaningFees"
                name="remaningFees"
                label="Remaining Fees"
                value={values.remaningFees}
                InputProps={{
                  readOnly: true,
                }}
                disabled={true}
                error={
                  touched.remaningFees &&
                  Boolean(errors.remaningFees)
                }
                helperText={
                  touched.remaningFees && errors.remaningFees
                }
              />
            </div>
          </div>

          {/* Payment Status */}
          <div className="form-group mt-4 d-flex">
            <div className="w-50">
              <FormControl fullWidth size="small">
                <InputLabel id="payment-status-label">
                  Payment Status
                </InputLabel>
                <Select
                  labelId="payment-status-label"
                  id="paymentStatus"
                  name="paymentStatus"
                  value={values.paymentStatus}
                  label="Payment Status"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.paymentStatus &&
                    Boolean(errors.paymentStatus)
                  }
                  disabled={true}
                >
                  <MenuItem value="false">Pending</MenuItem>
                  <MenuItem value="true">Complete</MenuItem>
                </Select>
                <FormHelperText className="danger">
                  <p style={{ color: "#d32f2f" }}>
                    {touched.paymentStatus &&
                      errors.paymentStatus}
                  </p>
                </FormHelperText>
              </FormControl>
            </div>

            <div className="w-50 ms-5">
              <TextField
                size="small"
                fullWidth
                id="dateofPayment"
                name="dateofPayment"
                label="Date of Payment"
                type="date"
                value={values.dateofPayment}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dateofPayment && Boolean(getRequiredError("dateofPayment"))}
                helperText={getRequiredError("dateofPayment")}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
              />
            </div>
          </div>

          {/* Additional Amount Received Field */}
          {parseFloat(values.additionalAmountReceived) > 0 && (
            <div className="form-group mt-4">
              <TextField
                size="small"
                fullWidth
                id="additionalAmountReceived"
                name="additionalAmountReceived"
                label="Additional Amount Received"
                value={values.additionalAmountReceived}
                InputProps={{
                  readOnly: true,
                }}
                disabled={true}
              />
            </div>
          )}

          {/* Payment Mode and Bank Name */}
          <div className="form-group mt-4 d-flex">
            <div className="w-50">
              <FormControl fullWidth size="small" error={touched.paymentMode && Boolean(getRequiredError("paymentMode"))}>
                <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                <Select
                  ref={paymentModeSelectRef}
                  labelId="payment-mode-label"
                  id="paymentMode"
                  name="paymentMode"
                  value={values.paymentModeId}
                  onChange={handlePaymentModeChange}
                  label="Payment Mode"
                  onBlur={handleBlur}
                >
                  {paymentModeList.map((mode) => (
                    <MenuItem
                      key={mode.paymentModeId}
                      value={mode.paymentModeId}
                    >
                      {mode.paymentModeName}
                    </MenuItem>
                  ))}
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
                <FormHelperText className="danger">
                  <p style={{ color: "#d32f2f" }}>
                    {getRequiredError("paymentMode")}
                  </p>
                </FormHelperText>
              </FormControl>
            </div>

            <div className="w-50 ms-5">
              <FormControl fullWidth size="small" error={touched.bankName && Boolean(getRequiredError("bankName"))}>
                <InputLabel id="bank-name-label">Bank Name</InputLabel>
                <Select
                  ref={bankSelectRef}
                  labelId="bank-name-label"
                  id="bankName"
                  name="bankName"
                  value={values.bankId}
                  onChange={handleBankChange}
                  label="Bank Name"
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>Select Bank</em>
                  </MenuItem>
                  {bankList.map((bank) => (
                    <MenuItem key={bank.bankId} value={bank.bankId}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText className="danger">
                  <p style={{ color: "#d32f2f" }}>
                    {getRequiredError("bankName")}
                  </p>
                </FormHelperText>
              </FormControl>
            </div>
          </div>

          {/* Date of Payment and Remarks */}
          <div className="form-group mt-4 d-flex">
            <div className="w-50 ">
              <TextField
                size="small"
                fullWidth
                id="remark"
                name="remark"
                label="Remark"
                value={values.remark}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.remark && Boolean(errors.remark)}
                helperText={touched.remark && errors.remark}
                multiline
                rows={3}
              />
            </div>

            <div className="w-50 ms-5"></div>
          </div>
        </div>
      )}

      {/* Document Upload Section */}
      {showFeesFields && (
        <div className="form-section mb-4">
          <h4 className="section-title mb-3">Document Upload</h4>

          {/* Payment Proofs Upload */}
          <div className="form-group mt-4">
            <div
              className="upload-area"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer.files);
                const validFiles = files.filter(
                  (file) =>
                    file.type === "image/jpeg" ||
                    file.type === "image/png" ||
                    file.type === "image/gif" ||
                    file.type === "application/pdf"
                );

                if (validFiles.length !== files.length) {
                  setErrors((prev) => ({ ...prev, paymentProof: "Some files were not valid (JPG, PNG, GIF, PDF)." }));
                }

                setValues((prev) => ({
                  ...prev,
                  paymentProof: [
                    ...prev.paymentProof,
                  ...validFiles,
                  ],
                }));
              }}
              style={{
                border: "2px dashed #ccc",
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#fafafa",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f5f5",
                },
                pointerEvents: "auto",
                opacity: 1,
              }}
            >
              <input
                type="file"
                id="paymentProof"
                name="paymentProof"
                accept="image/jpeg,image/png,image/gif,application/pdf"
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files);
                  const validFiles = files.filter(
                    (file) =>
                      file.type === "image/jpeg" ||
                      file.type === "image/png" ||
                      file.type === "image/gif" ||
                      file.type === "application/pdf"
                  );

                  if (validFiles.length !== files.length) {
                    setErrors((prev) => ({ ...prev, paymentProof: "Some files were not valid (JPG, PNG, GIF, PDF)." }));
                  }

                  setValues((prev) => ({
                    ...prev,
                    paymentProof: [
                      ...prev.paymentProof,
                    ...validFiles,
                    ],
                  }));
                }}
                onBlur={handleBlur}
                style={{ display: "none" }}
                disabled={false}
              />
              <label
                htmlFor="paymentProof"
                style={{ cursor: "pointer", width: "100%" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 16L12 8"
                      stroke="#1976d2"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 13L12 16 15 13"
                      stroke="#1976d2"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 16.7428C21.2215 15.734 22 14.2079 22 12.5C22 9.46243 19.5376 7 16.5 7C16.2815 7 16.0771 6.886 15.9661 6.69774C14.6621 4.48484 12.2544 3 9.5 3C5.35786 3 2 6.35786 2 10.5C2 12.5661 2.83545 14.4371 4.18695 15.7935"
                      stroke="#1976d2"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span style={{ color: "#1976d2", fontWeight: 500 }}>
                    Upload Payment Proofs
                  </span>
                  <span style={{ color: "#666", fontSize: "0.875rem" }}>
                    Drag & drop files here or click to browse
                  </span>
                  <span style={{ color: "#666", fontSize: "0.75rem" }}>
                    Supported formats: JPG, PNG, GIF, PDF
                  </span>
                </div>
              </label>
            </div>

            {/* File Preview Grid */}
            {values.paymentProof.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: "16px",
                  marginTop: "20px",
                }}
              >
                {values.paymentProof.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "8px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div
                      style={{
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        marginBottom: "8px",
                      }}
                    >
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                          stroke="#666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </div>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        const newFiles = [...values.paymentProof];
                        newFiles.splice(index, 1);
                        setValues((prev) => ({ ...prev, paymentProof: newFiles }));
                      }}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        minWidth: "24px",
                        width: "24px",
                        height: "24px",
                        padding: "0",
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {touched.paymentProof && Boolean(getRequiredError("paymentProof")) && (
              <FormHelperText error style={{ marginTop: "8px" }}>
                {getRequiredError("paymentProof")}
              </FormHelperText>
            )}
          </div>

          {/* Invoice Upload */}
          <div className="form-group mt-4">
            <div
              className="upload-area"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files[0];
                if (file) {
                  const isValid =
                    file.type ===
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                    file.type === "application/msword" ||
                    file.type === "application/pdf";
                  if (isValid) {
                    setValues((prev) => ({ ...prev, file }));
                  } else {
                    setErrors((prev) => ({ ...prev, file: "Please upload a valid Word document or PDF." }));
                    setValues((prev) => ({ ...prev, file: null }));
                  }
                }
              }}
              style={{
                border: "2px dashed #ccc",
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#fafafa",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f5f5",
                },
                pointerEvents: "auto",
                opacity: 1,
              }}
            >
              <input
                type="file"
                id="file"
                name="file"
                accept=".doc,.docx,.pdf"
                onChange={(event) => {
                  const file = event.target.files[0];
                  if (file) {
                    const isValid =
                      file.type ===
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                      file.type === "application/msword" ||
                      file.type === "application/pdf";
                    if (isValid) {
                      setValues((prev) => ({ ...prev, file }));
                    } else {
                      setErrors((prev) => ({ ...prev, file: "Please upload a valid Word document or PDF." }));
                      setValues((prev) => ({ ...prev, file: null }));
                    }
                  }
                }}
                onBlur={handleBlur}
                style={{ display: "none" }}
                disabled={false}
              />
              <label
                htmlFor="file"
                style={{ cursor: "pointer", width: "100%" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                      stroke="#666"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {values.file ? (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.875rem", color: "#333" }}>
                        {values.file.name}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        {(values.file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                  ) : (
                    <div style={{ flex: 1 }}>
                      <span style={{ color: "#1976d2", fontWeight: 500 }}>
                        Upload Invoice File
                      </span>
                      <span
                        style={{
                          color: "#666",
                          fontSize: "0.875rem",
                          display: "block",
                        }}
                      >
                        Drag & drop file here or click to browse
                      </span>
                      <span style={{ color: "#666", fontSize: "0.75rem" }}>
                        Supported formats: DOC, DOCX, PDF
                      </span>
                    </div>
                  )}
                  {values.file && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setValues((prev) => ({ ...prev, file: null }));
                      }}
                      style={{
                        minWidth: "32px",
                        width: "32px",
                        height: "32px",
                        padding: "0",
                      }}
                    >
                      ×
                    </Button>
                  )}
                </div>
              </label>
            </div>

            {/* Invoice File Preview */}
            {values.file && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                    stroke="#666"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.875rem", color: "#333" }}>
                    {values.file.name}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#666" }}>
                    {(values.file.size / 1024).toFixed(2)} KB
                  </div>
                </div>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setValues((prev) => ({ ...prev, file: null }));
                  }}
                  style={{
                    minWidth: "32px",
                    width: "32px",
                    height: "32px",
                    padding: "0",
                  }}
                >
                  ×
                </Button>
              </div>
            )}
            {touched.file && Boolean(getRequiredError("file")) && (
              <FormHelperText error style={{ marginTop: "8px" }}>
                {getRequiredError("file")}
              </FormHelperText>
            )}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div
        className="form-section"
        style={{ textAlign: "center", marginTop: "40px" }}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          style={{
            padding: "12px 48px",
            fontSize: "1.1rem",
            textTransform: "none",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          Submit Invoice
        </Button>
      </div>
    </form>
  );
};

export default Form;
